import { TrashIcon } from '@/icons';
import { getFileNameFromUrl, uploadFileToBucket } from '@/utils';
import { Flex, Form, type FormInstance } from 'antd';
import { useRef, useState, type ReactNode } from 'react';
import { useUploadFileMutation } from '../app/features/auth/auth.api';
import useAuth from '../hooks/useAuth';
import { toast } from '../lib/toast';

type Props = {
  form: FormInstance<any>;
  name: string;
  title?: string;
  value?: string[];
  accept?: string;
  icon?: ReactNode;
  maxSize?: number; // MB
  disabled?: boolean;
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  onChange?: (urls: string[]) => void;
};

export default function MultiFileUploader({
  name,
  form,
  title,
  onChange,
  disabled,
  description,
  maxSize = 5,
  accept = '.png,.jpeg,.svg,.jpg,.pdf,.doc,.docx',
  errorMessage = 'File upload failed',
}: Props) {
  const { access_token } = useAuth();
  const [uploadFile, { isLoading }] = useUploadFileMutation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const loading = isLoading || isUploading;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !access_token) return;

    const maxBytes = maxSize * 1024 * 1024;
    const validFiles = files.filter((file) => {
      if (file.size > maxBytes) {
        toast('error', `File "${file.name}" exceeds ${maxSize} MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setIsUploading(true);

    try {
      const currentUrls = form?.getFieldValue(name) || [];
      const newUrls: string[] = [];

      for (const file of validFiles) {
        let fileUrl: string;
        if (access_token) {
          const res = await uploadFileToBucket(file, access_token);
          if (!res.success) {
            throw new Error(res.error);
          }
          fileUrl = res.url as string;
        } else {
          const formData = new FormData();
          formData.append('file', file);
          const res = await uploadFile(formData).unwrap();
          fileUrl = res?.Location;
        }
        newUrls.push(fileUrl);
      }

      const updatedUrls = [...currentUrls, ...newUrls];
      form?.setFieldValue(name, updatedUrls);
      onChange?.(updatedUrls);
    } catch {
      toast('error', errorMessage);
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = ''; // reset input
    }
  };

  const removeFile = (index: number) => {
    const currentUrls = form?.getFieldValue(name) || [];
    const updatedUrls = currentUrls.filter((_: any, i: number) => i !== index);
    form?.setFieldValue(name, updatedUrls);
    onChange?.(updatedUrls);
  };

  const watchedFiles = Form.useWatch(name, form) || [];

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="!hidden"
        disabled={disabled || loading}
        onChange={handleFileChange}
      />

      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => inputRef.current?.click()}
        className="w-full p-6 min-h-[135px] rounded-lg bg-theme-gray-99 dark:bg-theme-slate-900 hover:bg-theme-gray-98 dark:hover:bg-theme-slate-800 active:bg-theme-gray-99 dark:active:bg-theme-slate-900 transition-all border-2 border-dashed border-theme-gray-800 cursor-pointer"
      >
        <div>
          <h4 className="text-theme-gray-50 dark:text-white text-base font-semibold text-center">
            {isUploading ? 'Uploading...' : title || 'Upload documents'}
          </h4>
          {description && (
            <p className="text-theme-gray-60 dark:text-white text-base font-normal text-center">
              {description}
            </p>
          )}
        </div>
      </button>

      {watchedFiles.length > 0 && (
        <div className="space-y-2.5 mt-4">
          {watchedFiles.map((url: string, index: number) => (
            <Flex
              key={index}
              gap={16}
              align="center"
              justify="space-between"
              className="bg-theme-gray-96 dark:bg-theme-slate-900 px-4 py-2 rounded-md h-auto border border-theme-slate-800"
            >
              <Flex gap={6} align="center" className="flex-1 truncate">
                <span className="text-sm text-theme-slate-800 dark:text-white font-medium truncate">
                  {getFileNameFromUrl(url)}
                </span>
              </Flex>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="text-theme-error-500 hover:text-theme-error-600 transition-colors"
              >
                <TrashIcon size={16} />
              </button>
            </Flex>
          ))}
        </div>
      )}
    </>
  );
}
